import React from 'react';
import PropTypes from 'prop-types';

// Services
import FileUpload from '../../../services/FileUpload';

// Common components
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';

// Component styles
import './dragArea.scss';

// Constants
import appErrorMessages from '../../../constants/appErrors';

/**
 * This component shows the drag area allowing the image upload process.
 * - Handle errors when the upload process fails.
 * - Render a draggable area for the image drag and drop.
 * - Upload a file with using the FileUpload Service.
 * @extends {Component}
 */
class DragArea extends React.Component {
  constructor(props) {
    super(props);

    /**
     * @type {Object}
     */
    this.document = document;

    /**
     * Instance of the FileUpload service. It will validate and upload the image.
     * @type {FileUpload}
     */
    this.fileUpload = new FileUpload();

    // Context bindings for functions.
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleFilePickerClick = this.handleFilePickerClick.bind(this);
  }

  /**
   * When the component is mounted, it will add an event listener on the element document in
   * order to track the drag area actions.
   */
  componentDidMount() {
    [this.dragAreaElement] = this.document.getElementsByClassName('wtfDragArea');

    this.document.addEventListener('dragover', this.handleDragover, false);
    this.dragAreaElement.addEventListener('dragenter', this.handleDrag, false);
    this.dragAreaElement.addEventListener('dragleave', this.handleDrag, false);
    this.dragAreaElement.addEventListener('drop', this.handleDrop, false);
  }

  /**
   * When the component is unmounted, it will remove all the event listener that was added to the element document.
   */
  componentWillUnmount() {
    this.document.removeEventListener('dragover', this.handleDragover, false);
    this.dragAreaElement.removeEventListener('dragenter', this.handleDrag, false);
    this.dragAreaElement.removeEventListener('dragleave', this.handleDrag, false);
    this.dragAreaElement.removeEventListener('drop', this.handleDrop, false);
  }

  /**
   * Takes an event and calls `preventDefault` and `stopPropagation` on it. It will avoid opening the image
   * in the browser when the user drag over the drag area.
   * @param {Event} event A native `Event` instance.
   */
  handleDragover(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * It handle the drag behavior, it will toggle the drag area highlight class.
   */
  handleDrag(event) {
    event.preventDefault();
    event.stopPropagation();

    this.dragAreaElement.classList.toggle('-dragAreaHighlight');
  }

  /**
   * It handle the drop behavior.
   * - Toggle the highlight class.
   * - Avoid event propagation.
   * - If the imageUrl is already defined means the user is uploading another image. In this case we should
   * revoke the old ObjectUrl.
   * - Get the image file using the `FileUpload` service.
   */
  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    this.dragAreaElement.classList.toggle('-dragAreaHighlight');

    this.fileUpload.getFileImage(event.dataTransfer.files, event.dataTransfer.items)
      .then((data) => {
        /**
         * If the image upload process was ok, if exists remove the previous objectUrl
         * and then update the new one.
         */
        if (this.props.imageUrl) {
          this.fileUpload.revokeObjectUrl(this.props.imageUrl);
        }

        this.props.onImageUploaded(data);
      })
      .catch((error) => {
        this.props.onError(error);
      });
  }
  /**
   * It handles the click on the file picker button. Creates a input type file and opens the file picker
   * dialog. Once a file is selected it checks if already exists an image, if this is the case then it
   * revokes that url.
   * @param {Event} e Click native Event object
   */
  handleFilePickerClick(e) {
    e.preventDefault();
    const [fileInput] = document.getElementsByClassName('wtfDragArea_description_filePicker');
    fileInput.onchange = (event) => {
      this.fileUpload.getFileImage(event.target.files)
        .then((data) => {
          /**
           * If the image upload process was ok, if exists remove the previous objectUrl
           * and then update the new one.
           */
          if (this.props.imageUrl) {
            this.fileUpload.revokeObjectUrl(this.props.imageUrl);
          }

          this.props.onImageUploaded(data);
        })
        .catch((error) => {
          this.props.onError(error);
        });
    };
    fileInput.click();
  }
  /**
   * If errorCode is presented, render the error message component.
   */
  renderErrorMessage() {
    let dataToRender = null;

    if (this.props.errorCode) {
      const errorMessage = appErrorMessages.upload[this.props.errorCode] ?
        appErrorMessages.upload[this.props.errorCode] :
        appErrorMessages.upload.DEFAULT;

      dataToRender = (
        <div className='wtfDragArea_error'>
          <ErrorMessage text={errorMessage} onClose={this.props.onErrorClose} />
        </div>
      );
    }

    return dataToRender;
  }

  render() {
    return (
      <div className='wtfDragArea'>
        {this.renderErrorMessage()}
        <div className='wtfDragArea_description'>
          <span className='wtfDragArea_description_title'>
            Drop an image here to identify the fonts!
          </span>
          <button onClick={this.handleFilePickerClick} className='wtfDragArea_description_link'>
            or click here to upload an image
          </button>
          <input
            type='file'
            className='wtfDragArea_description_filePicker'
            accept='.png,.jpg,.jpeg,image/png,image/jpg,image/jpeg'
          />
          <span className='wtfDragArea_description_files'>
            PNG or JPG only
          </span>
        </div>
      </div>
    );
  }
}

/**
 * Declare the DragArea component PropTypes for better validation.
 * @type {Object}
 * @property {String}   errorCode       If exists, it matches the error dictionary and shows the error message
 *                                      component.
 * @property {Function} onImageUploaded This callback will update the file when the validation is ok.
 * @property {Function} onError         This callback will update the error code when the uploaded files is invalid.
 * @property {String}   imageUrl        It is used in the handleDrop handler to validate if an existing image object
 *                                      url is presented in order to revoke the object url before creating a new one.
 */
DragArea.propTypes = {
  errorCode: PropTypes.string.isRequired,
  onImageUploaded: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  imageUrl: PropTypes.string.isRequired,
  onErrorClose: PropTypes.func.isRequired,
};

export default DragArea;



// WEBPACK FOOTER //
// ./src/components/Upload/DragArea/DragArea.js